import { useId, useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { CM_CLUSTER_MAX_CASES, CM_CLUSTER_MAX_HOPS } from '@bloo-team/api-client'
import type { CmCase, CmScope, CmStatus } from '@bloo-team/api-client'
import { useCmCluster } from '../../lib/case-activity'
import { clusterGroups, clusterParents, type ClusterParent } from '../../lib/case-cluster'
import { caseLabel, caseRef, sevKey, statusLabel } from '../../lib/case-mgmt'
import type { NetworkCase } from '../../lib/case-network-graph'
import { CaseNetworkGraph } from './CaseNetworkGraph'
import { ClusterGraph, LINK_COLOR } from './ClusterGraph'

/**
 * A case's Clusters tab — layout B (docs/cases.md §Case page · Clusters).
 * The cases reachable from this one through active relations (`GET
 * /cases/{id}/cluster`), within 1, 2 or 3 hops (2 by default, as the
 * service), at most 100 cases — a note says when the cap cut it short.
 * Left: the graph (ClusterGraph) with its key and the hop picker. Right:
 * *Cases in this cluster* — this case, then each hop; a row's number is the
 * shared entities on its link one hop nearer this case (the strongest).
 * Selecting a case in either place selects it in both; the strip under the
 * graph names it and opens it.
 *
 * **Cases | Signals & entities** (Cases first). The second view
 * (CaseNetworkGraph) draws the same cluster's cases, within the same hops,
 * as dotted boxes around their signals and entities.
 */

const HOPS = Array.from({ length: CM_CLUSTER_MAX_HOPS }, (_, i) => i + 1)
const DEFAULT_HOPS = 2

export function ClustersTab({
  scope,
  caseId,
  self,
  statuses,
  onOpenCase,
  onOpenSignal,
}: {
  scope: CmScope
  caseId: string
  /** This case — the graph's own box. */
  self: Pick<CmCase, 'id' | 'caseNumber' | 'title' | 'status' | 'severity'>
  statuses: CmStatus[]
  onOpenCase: (ref: string) => void
  onOpenSignal: (uuid: string) => void
}) {
  const uid = useId()
  const [view, setView] = useState<'cases' | 'graph'>('cases')
  const [hops, setHops] = useState(DEFAULT_HOPS)
  const [selected, setSelected] = useState<string | null>(null)
  const q = useCmCluster(scope, caseId, hops)
  const cluster = q.data
  const groups = useMemo(() => (cluster ? clusterGroups(cluster) : []), [cluster])
  const parents = useMemo(() => (cluster ? clusterParents(cluster) : new Map<string, ClusterParent>()), [cluster])
  const statusText = useMemo(() => (s: string) => statusLabel(statuses, s), [statuses])
  const picked = cluster?.nodes.find((n) => n.id === selected) ?? null
  const alone = cluster !== undefined && cluster.nodes.length <= 1
  const networkCases = useMemo<NetworkCase[]>(
    () =>
      cluster
        ? [...cluster.nodes]
            .sort((a, b) => a.distance - b.distance)
            .map((n) => ({ id: n.id, caseNumber: n.caseNumber, title: n.title, status: n.status, severity: n.severity, kind: n.id === self.id ? 'this' : 'cluster', hop: n.distance, linkedBy: null }))
        : [],
    [cluster, self.id],
  )

  const viewBar = (
    <div className="hud-row hud-row-between cs-net-bar">
      <span className="bui-toggles" role="group" aria-label="View">
        {(['cases', 'graph'] as const).map((v) => (
          <button key={v} type="button" className="bui-toggle" aria-pressed={view === v} onClick={() => setView(v)}>
            {v === 'cases' ? 'Cases' : 'Signals & entities'}
          </button>
        ))}
      </span>
      {view === 'graph' ? (
        <span className="hud-row cs-cl-hops">
          <span className="hud-eyebrow" id={`${uid}-net-hops`}>
            hops
          </span>
          <span className="bui-toggles" role="group" aria-labelledby={`${uid}-net-hops`}>
            {HOPS.map((h) => (
              <button key={h} type="button" className="bui-toggle" aria-pressed={hops === h} onClick={() => setHops(h)}>
                {h}
              </button>
            ))}
          </span>
        </span>
      ) : null}
    </div>
  )

  if (view === 'graph') {
    return (
      <div className="cs-net-tab">
        {viewBar}
        {cluster?.truncated ? (
          <p className="sy-warning" role="status">
            Showing the first {CM_CLUSTER_MAX_CASES} cases — more are related within {hops} hop{hops === 1 ? '' : 's'}.
          </p>
        ) : null}
        {q.isError ? (
          <p className="sy-error" role="alert">
            {q.error.message}
          </p>
        ) : !cluster ? (
          <p className="hud-unit" role="status">
            Loading the cluster…
          </p>
        ) : alone ? (
          <p className="hud-unit cs-placeholder">No related cases — this case is not in a cluster.</p>
        ) : (
          <CaseNetworkGraph
            key={hops}
            scope={scope}
            cases={networkCases}
            statuses={statuses}
            onOpenCase={(c) => onOpenCase(caseRef(c))}
            onOpenSignal={onOpenSignal}
            label={`Cluster graph — the cases within ${hops} hop${hops === 1 ? '' : 's'}, each a dotted box around its signals and their entities`}
          />
        )}
      </div>
    )
  }

  return (
    <div className="cs-net-tab">
      {viewBar}
    <div className="cs-cl">
      <section className="hud-panel cs-cl-main" aria-label="Cluster graph">
        <div className="hud-row hud-row-between cs-cl-head">
          <ul className="agg-legend cs-cl-key" aria-label="Key">
            <li className="agg-legend-item">
              <svg width="22" height="6" aria-hidden="true">
                <line x1="1" y1="3" x2="21" y2="3" stroke={LINK_COLOR.auto} strokeWidth="2.5" strokeLinecap="round" />
              </svg>
              <span className="agg-legend-v">auto link</span>
            </li>
            <li className="agg-legend-item">
              <svg width="22" height="6" aria-hidden="true">
                <line x1="1" y1="3" x2="21" y2="3" stroke={LINK_COLOR.manual} strokeWidth="2.5" strokeDasharray="5 3" />
              </svg>
              <span className="agg-legend-v">manual link</span>
            </li>
            <li className="agg-legend-item">
              <span className="agg-legend-v muted">line weight = shared entities</span>
            </li>
          </ul>
          <span className="hud-row cs-cl-hops">
            <span className="hud-eyebrow" id={`${uid}-hops`}>
              hops
            </span>
            <span className="bui-toggles" role="group" aria-labelledby={`${uid}-hops`}>
              {HOPS.map((h) => (
                <button key={h} type="button" className="bui-toggle" aria-pressed={hops === h} onClick={() => setHops(h)}>
                  {h}
                </button>
              ))}
            </span>
          </span>
        </div>
        {cluster?.truncated ? (
          <p className="sy-warning" role="status">
            Showing the first {CM_CLUSTER_MAX_CASES} cases — more are related within {hops} hop{hops === 1 ? '' : 's'}.
          </p>
        ) : null}
        {q.isError ? (
          <p className="sy-error" role="alert">
            {q.error.message}
          </p>
        ) : !cluster ? (
          <p className="hud-unit" role="status">
            Loading the cluster…
          </p>
        ) : alone ? (
          <p className="hud-unit cs-placeholder">No related cases — this case is not in a cluster.</p>
        ) : (
          <ClusterGraph key={hops} cluster={cluster} startId={caseId} selected={selected} onSelect={setSelected} statusText={statusText} />
        )}
        {picked ? (
          <div className="hud-row cs-cl-foot" role="status" aria-label="Selected case">
            <span className="mono muted">{caseLabel(picked)}</span>
            <span className="cs-cl-foot-title">{picked.title}</span>
            <span className="cs-status">{statusText(picked.status)}</span>
            <span className="mono muted">{picked.id === caseId ? 'this case' : `hop ${picked.distance}`}</span>
            {picked.id !== caseId ? (
              <Link
                to="/$clusterId/$scopeId/vantage/cases/$caseId"
                params={{ clusterId: scope.clusterId, scopeId: scope.scopeId, caseId: caseRef(picked) }}
                className="cs-cl-open"
              >
                Open case
              </Link>
            ) : null}
          </div>
        ) : null}
      </section>

      <aside className="hud-panel cs-cl-side" aria-labelledby={`${uid}-cl-h`}>
        <h2 id={`${uid}-cl-h`} className="cs-h">
          Cases in this cluster <span className="sg-queue-n">{cluster?.nodes.length ?? 0}</span>
        </h2>
        {groups.map((g) => (
          <div key={g.distance} className="cs-cl-group">
            <span className="hud-eyebrow">{g.label}</span>
            <ul aria-label={g.label}>
              {g.nodes.map((n) => {
                const shared = parents.get(n.id)?.shared
                return (
                  <li key={n.id}>
                    <button type="button" className="cs-cl-row" aria-pressed={selected === n.id} onClick={() => setSelected(n.id)}>
                      <span className="sp-sev" data-severity={sevKey(n.severity)} aria-hidden="true" />
                      <span className="mono muted">{caseLabel(n)}</span>
                      <span className="cs-cl-row-title">{n.title}</span>
                      <span className="mono muted" title={shared !== undefined ? `${shared} shared with the case one hop nearer` : undefined}>
                        {shared ?? ''}
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}
      </aside>
    </div>
    </div>
  )
}
